﻿import React, { Ref, useEffect, useRef, useState } from 'react';
import { InputRef, InputProps, Input } from 'antd';

import { CoordinateNotation } from '../../interfaces/CoordinateNotation';
import CoordinateUtilities from '../../utils/CoordinateUtilities';

export interface Props extends Omit<InputProps, "onChange" | "onPressEnter" | "onBlur" | "value" | "enterKeyHint"> {
    ref?: Ref<InputRef>
    value: number[] | undefined,

    onChange?: (lonLat: number[] | undefined, changed: boolean) => void,
    onBlur?: (lonLat: number[] | undefined, changed: boolean) => void,
    onPressEnter?: (lonLat: number[] | undefined, changed: boolean) => void,

    /** Default is Deg. */
    notation?: CoordinateNotation,
    /** Default is 500ms. */
    onChangeDelay?: number
    /** Default is 2 */
    decimals?: number,

    processInput?: (lonLat: number[]) => number[],
    processOutput?: (lonLat: number[]) => number[],
}

/**
 * Input field for a lonLat coordinate, view value is formatted according to notation property.
 * */
const CoordinateInput = React.forwardRef<InputRef, Props>(({
    value: modelValue,
    onChange,
    onBlur,
    onPressEnter,
    notation = "Deg",
    onChangeDelay = 500,
    decimals = 2,
    processInput,
    processOutput,
    ...rest
}, ref) => {

    const changeTimeout = useRef<number>();
    const [viewValue, setViewValue] = useState<string>("");

    useEffect(() => {
        setViewValue(toString(modelValue, notation));
    }, [modelValue, notation]);

    const toString = (lonLat: number[] | undefined, notation: CoordinateNotation) => {
        if (lonLat)
            return CoordinateUtilities.formatLonLatCoordinate(lonLat, notation, decimals)
        return "";
    }

    const fromInput = (coordinate: number[] | undefined) => {

    }

    const toOutput = (lonLat: number[] | undefined) => {
        if (processOutput && lonLat)
            return processOutput(lonLat);
        return lonLat;        
    }

    const hasChanged = (lonLat: number[] | undefined) => {
        const newString = toString(lonLat, notation);
        const currentString = toString(modelValue, notation);
        return newString !== currentString;
    }

    const handleChange = (value: string) => {
        if (value !== viewValue) {
            setViewValue(value);

            if (onChange) {
                window.clearTimeout(changeTimeout.current);
                changeTimeout.current = window.setTimeout(() => {
                    const coord = CoordinateUtilities.parseLonLatCoordinate(value);
                    onChange(toOutput(coord), hasChanged(coord));
                }, onChangeDelay)
            }
        }
    }

    const handleBlur = (value: string) => {
        const coord = CoordinateUtilities.parseLonLatCoordinate(value);
        setViewValue(toString(coord, notation));
        if (onBlur)
            onBlur(toOutput(coord), hasChanged(coord));
    }

    const handleOnEnter = (value: string) => {
        const coord = CoordinateUtilities.parseLonLatCoordinate(value);
        setViewValue(toString(coord, notation));
        if (onPressEnter)
            onPressEnter(toOutput(coord), hasChanged(coord));
    }

    return (
        <Input type="text"
            value={viewValue}
            onChange={e => handleChange(e.target.value)}
            onBlur={e => handleBlur(e.target.value)}
            onPressEnter={e => handleOnEnter(e.currentTarget.value)}
            {...rest}
            ref={ref}
        />
    )
});

export default CoordinateInput;